import { Button, ButtonProps, Tooltip, TooltipProps } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";

import { usePostSubmit } from "@/common/hooks/usePostSubmit";

type FormSubmitProps = ButtonProps & {
  postSubmitChildren?: React.ReactNode;
  showPostSubmit?: boolean;
  checkForDirty?: boolean;
  disableOnSuccess?: boolean;
  disableOnPristine?: boolean;
  showTooltipOnPristine?: boolean;
  _tooltip?: Omit<TooltipProps, "children">;
};

const FormSubmit = ({
  children = "Save",
  loadingText = undefined,
  postSubmitChildren = "Saved!",
  showPostSubmit = false,
  checkForDirty = true,
  disableOnSuccess = false,
  disableOnPristine = false,
  isDisabled,
  isLoading,
  showTooltipOnPristine = false,
  _tooltip,
  ...rest
}: FormSubmitProps) => {
  const {
    formState: { isSubmitting, isSubmitSuccessful, isDirty, isValid },
  } = useFormContext();

  const [postSubmit] = usePostSubmit({
    enabled: showPostSubmit,
  });

  const isDimmed =
    isSubmitting ||
    !isValid ||
    (checkForDirty && !isDirty) ||
    (showPostSubmit && postSubmit);

  const isDisabledOnSubmit = disableOnSuccess && isSubmitSuccessful;
  const isDisabledOnDirty = disableOnPristine && !isDirty;
  const shouldShowTooltipOnPristine = showTooltipOnPristine && !isDirty;

  return (
    <Tooltip
      isDisabled={!shouldShowTooltipOnPristine}
      label="No changes yet"
      placement="right"
      hasArrow
      {..._tooltip}
    >
      <Button
        isDisabled={isDisabledOnSubmit || isDisabledOnDirty || isDisabled}
        isLoading={isSubmitting || isLoading}
        loadingText={loadingText}
        type="submit"
        variant={isDimmed ? "solidDimmed" : "solid"}
        {...rest}
      >
        {showPostSubmit && postSubmit ? postSubmitChildren : children}
      </Button>
    </Tooltip>
  );
};

export default FormSubmit;
